import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BEST_MATCHES_KEY } from '../FollowUpScheduling/FollowUpChips';
import { selectBestMatch } from '../FollowUpScheduling/followUp/bestMatch.util';

export interface UseFollowUpSchedulingProps {
  series: FollowUpSeries[];
  bookableWeekCount: number;
  preferences: PatientAvailabilityPreferences | null;
  timezone?: string;
  onAutoSelect?: (selected: FollowUpSeries) => void;
}


export interface UseFollowUpSchedulingResult {
  followUpsByWeek: Map<number, FollowUpOpening[]>;
  selectedFollowUps: FollowUpOpening[];
  followUpAppointmentCount: number;
  activeSeriesKey: string | null;
  bestMatch: FollowUpSeries | null;
  onBestMatchSelect: () => void;
  onSeriesSelect: (series: FollowUpSeries) => void;
  onSeriesClear: () => void;
  onSlotSelect: (weekIndex: number, opening: FollowUpOpening) => void;
  rejectSelection: () => void;
  reinitializeSelection: () => void;
  markAccepted: () => void;
}

const applySeriesSlots = (
  selected: FollowUpSeries,
): Map<number, FollowUpOpening[]> => {
  const next = new Map<number, FollowUpOpening[]>();
  for (const [weekIndex, opening] of selected.openingsByWeek) {
    next.set(weekIndex, [opening]);
  }
  return next;
};

const useFollowUpScheduling = ({
  series,
  bookableWeekCount,
  preferences,
  timezone,
  onAutoSelect,
}: UseFollowUpSchedulingProps): UseFollowUpSchedulingResult => {
  const [followUpsByWeek, setFollowUpsByWeek] = useState<
    Map<number, FollowUpOpening[]>
  >(new Map());
  const [activeSeriesKey, setActiveSeriesKey] = useState<string | null>(null);
  // Pending = no explicit decision made by the user yet for this step. An
  // auto-selected best match is still pending until the user explicitly
  // accepts it, rejects it (skip), or overrides it — at which point it's
  // locked and must not be disturbed by a later auto-select.
  const isPending = useRef(true);
  const onAutoSelectRef = useRef(onAutoSelect);
  onAutoSelectRef.current = onAutoSelect;

  const markAccepted = useCallback(() => {
    isPending.current = false;
  }, []);

  const bestMatch = useMemo(
    () => selectBestMatch(series, bookableWeekCount, preferences, timezone),
    [series, bookableWeekCount, preferences, timezone],
  );

  /**
   * Auto-select best match while the selection is still pending (no explicit user
   * decision yet). Once accepted/rejected/overridden, auto-select must not run again.
   */
  useEffect(() => {
    if (!isPending.current || !bestMatch) return;

    setActiveSeriesKey(BEST_MATCHES_KEY);
    setFollowUpsByWeek(applySeriesSlots(bestMatch));
    onAutoSelectRef.current?.(bestMatch);
  }, [bestMatch]);

  const onSeriesSelect = useCallback(
    (selected: FollowUpSeries) => {
      // If the selected recurring chip produces the same slots as best match,
      // just swap the key without changing followUpsByWeek
      markAccepted();
      if (
        activeSeriesKey === BEST_MATCHES_KEY &&
        selected.key === bestMatch?.key
      ) {
        setActiveSeriesKey(selected.key);
        return;
      }

      setActiveSeriesKey(selected.key);
      setFollowUpsByWeek(applySeriesSlots(selected));
    },
    [activeSeriesKey, bestMatch, markAccepted],
  );

  const onBestMatchSelect = useCallback(() => {
    if (!bestMatch) return;
    markAccepted();
    setActiveSeriesKey(BEST_MATCHES_KEY);
    setFollowUpsByWeek(applySeriesSlots(bestMatch));
  }, [bestMatch, markAccepted]);

  const onSeriesClear = useCallback(() => {
    markAccepted();
    setActiveSeriesKey(null);
    setFollowUpsByWeek(new Map());
  }, [markAccepted]);

  const onSlotSelect = useCallback(
    (weekIndex: number, opening: FollowUpOpening) => {
      markAccepted();
      setFollowUpsByWeek((current) => {
        const next = new Map(current);
        const existing = next.get(weekIndex) ?? [];
        const isSelected = existing.some(
          (o) =>
            o.startTime === opening.startTime &&
            o.providerId === opening.providerId,
        );
        const updated = isSelected
          ? existing.filter(
            (o) =>
              !(
                o.startTime === opening.startTime &&
                o.providerId === opening.providerId
              ),
          )
          : [...existing, opening].sort(
            (a, b) =>
              new Date(a.startTime).getTime() -
              new Date(b.startTime).getTime(),
          );
        if (updated.length) next.set(weekIndex, updated);
        else next.delete(weekIndex);
        return next;
      });

      // Deselect chip if the manually selected slot is not part of the active series
      setActiveSeriesKey((currentKey) => {
        if (!currentKey) return null;
        const activeSeries =
          currentKey === BEST_MATCHES_KEY
            ? bestMatch
            : series.find((s) => s.key === currentKey);
        if (!activeSeries) return null;

        const seriesOpening = activeSeries.openingsByWeek.get(weekIndex);
        const isPartOfSeries =
          seriesOpening?.startTime === opening.startTime &&
          seriesOpening?.providerId === opening.providerId;

        return isPartOfSeries ? currentKey : null;
      });
    },
    [bestMatch, markAccepted, series],
  );

  // Clears to zero selections and locks — unlike markAccepted, must not
  // re-open to auto-select on return.
  const rejectSelection = useCallback(() => {
    setFollowUpsByWeek(new Map());
    setActiveSeriesKey(null);
    markAccepted();
  }, [markAccepted]);

  // Clears to zero selections and returns to pending, eligible for auto-select.
  const reinitializeSelection = useCallback(() => {
    setFollowUpsByWeek(new Map());
    setActiveSeriesKey(null);
    isPending.current = true;
  }, []);

  const selectedFollowUps = useMemo(
    () =>
      Array.from(followUpsByWeek.entries())
        .sort(([a], [b]) => a - b)
        .flatMap(([, openings]) => openings),
    [followUpsByWeek],
  );

  const followUpAppointmentCount = useMemo(
    () =>
      Array.from(followUpsByWeek.values()).reduce(
        (total, openings) => total + openings.length,
        0,
      ),
    [followUpsByWeek],
  );

  return {
    followUpsByWeek,
    selectedFollowUps,
    followUpAppointmentCount,
    activeSeriesKey,
    bestMatch: bestMatch ?? null,
    onBestMatchSelect,
    onSeriesSelect,
    onSeriesClear,
    onSlotSelect,
    rejectSelection,
    reinitializeSelection,
    markAccepted,
  };
};

export default useFollowUpScheduling;
